/*
 * Naturlabs motion system (JS side). CSS side: assets/nl-motion.css
 *
 * Public API (window.NL.motion):
 *   NL.motion.reduced          boolean, live value of prefers-reduced-motion or the theme motion setting
 *   NL.motion.reveal(root)     scan a subtree for [data-reveal] / [data-reveal-stagger] / [data-count-up] / [data-magnetic]
 *   NL.motion.countUp(el)      animate a number element now
 *
 * Attributes:
 *   data-reveal="" | "fade" | "scale"     reveal once when 10 % visible
 *   data-reveal-delay="120"               extra delay in ms
 *   data-reveal-stagger                   reveal direct children in sequence
 *   data-count-up data-count-to="1500" [data-count-from="0"] [data-count-decimals="0"] [data-count-duration="1400"]
 *   data-magnetic                         subtle pointer pull (fine pointers only)
 *
 * Header behaviour (the header section wrapper .nl-header-section):
 *   data-scrolled  set when the page is scrolled more than 24px (the bar turns solid, see nl-header.css section 2)
 *   data-hidden    set while scrolling down (only when the header declares data-hide-on-scroll)
 *   --nl-header-offset on :root = visible header height (0 when hidden), for sticky elements
 */
(() => {
  'use strict';

  const NL = (window.NL = window.NL || {});
  const root = document.documentElement;
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');

  const motionOff = () => mqReduced.matches || root.classList.contains('nl-motion-off');
  const designMode = () => root.classList.contains('nl-design-mode');

  /* ------------------------------------------------------------------
   * Reveal
   * Hiding is gated by html.nl-motion-ready (set by this script once it runs), so content stays visible when the
   * script is slow or fails. Elements already inside the viewport when the script boots are marked revealed
   * immediately (no animation, no flash). Timings: 560 ms, stagger 60 ms capped at 6 steps, so no element waits
   * longer than about 600 ms (see nl-motion.css section 1).
   * data-reveal-words  splits the direct text of a heading into word spans that rise in one after another
   * data-scale-in      media wrapper whose image settles from a slight zoom (scroll driven in CSS where supported)
   * ------------------------------------------------------------------ */
  const REVEAL_SELECTOR = '[data-reveal], [data-reveal-stagger], [data-reveal-words], [data-scale-in]';
  const STAGGER_STEPS = 6;

  const finishReveal = (el) => {
    el.classList.add('is-revealed');
    const isStagger = el.hasAttribute('data-reveal-stagger');
    const isWords = el.hasAttribute('data-reveal-words');
    const count = isStagger ? Math.min(el.children.length, STAGGER_STEPS + 1) : 1;
    const stagger = 60;
    const delay = parseFloat(el.dataset.revealDelay || 0);
    let total = 620 + delay + (isStagger ? (count - 1) * stagger : 0);
    if (isWords) total += 400;
    if (el.hasAttribute('data-scale-in')) total = 1300;
    window.setTimeout(() => el.classList.add('is-reveal-done'), total);
  };

  // Wrap every word of the element's own text nodes in a span; child elements (sup, br, links) stay whole.
  // Non-breaking spaces stay inside their word, so Czech one letter prepositions keep their partner.
  const splitWords = (el) => {
    if (el.dataset.wordsSplit) return;
    el.dataset.wordsSplit = '1';
    const label = el.textContent.replace(/\s+/g, ' ').trim();
    if (label && !el.hasAttribute('aria-label')) el.setAttribute('aria-label', label);
    let index = 0;
    Array.from(el.childNodes).forEach((node) => {
      if (node.nodeType === 3) {
        const parts = node.textContent.split(/([ \t\n\r]+)/);
        const frag = document.createDocumentFragment();
        parts.forEach((part) => {
          if (!part) return;
          if (/^[ \t\n\r]+$/.test(part)) {
            frag.appendChild(document.createTextNode(' '));
            return;
          }
          const span = document.createElement('span');
          span.className = 'nl-word';
          span.setAttribute('aria-hidden', 'true');
          span.style.setProperty('--nl-word-index', String(Math.min(index, 10)));
          span.textContent = part;
          frag.appendChild(span);
          index += 1;
        });
        node.replaceWith(frag);
      } else if (node.nodeType === 1 && node.tagName !== 'BR') {
        node.classList.add('nl-word');
        node.setAttribute('aria-hidden', 'true');
        node.style.setProperty('--nl-word-index', String(Math.min(index, 10)));
        index += 1;
      }
    });
  };

  const prepare = (el) => {
    if (el.dataset.revealDelay) el.style.setProperty('--nl-reveal-delay', `${parseFloat(el.dataset.revealDelay)}ms`);
    if (el.hasAttribute('data-reveal-stagger')) {
      Array.from(el.children).forEach((child, i) => child.style.setProperty('--nl-reveal-index', String(i)));
    }
    if (el.hasAttribute('data-reveal-words') && !motionOff() && !designMode()) splitWords(el);
  };

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              revealObserver.unobserve(entry.target);
              finishReveal(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0 }
        )
      : null;

  // True until the first scan has finished: anything visible at that moment shows at once instead of animating
  let booting = true;

  /* Boot without reading layout from script (perf lane 16. 10. 2026): the first IntersectionObserver answer, which the
     browser computes during its own rendering step, says what is already on screen (shown at once, no animation).
     Measuring every element at boot forced a full style and layout pass in the middle of the deferred scripts
     (100 to 150 ms on phones), then the next script changed the DOM and the page was laid out again.
     html.nl-motion-ready, which lets CSS hide not yet revealed elements, is set only after that first answer, so
     nothing on screen ever flashes hidden. */
  let bootPending = 0;
  const markReady = () => root.classList.add('nl-motion-ready');
  const bootObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const el = entry.target;
            bootObserver.unobserve(el);
            bootPending -= 1;
            if (entry.isIntersecting) {
              el.classList.add('is-revealed', 'is-reveal-done');
            } else {
              prepare(el);
              revealObserver.observe(el);
            }
          });
          if (bootPending <= 0) markReady();
        })
      : null;

  const observeReveal = (el) => {
    if (el.dataset.revealBound) return;
    el.dataset.revealBound = '1';
    if (!revealObserver || motionOff() || designMode()) {
      el.classList.add('is-revealed', 'is-reveal-done');
      return;
    }
    if (booting && bootObserver) {
      bootPending += 1;
      bootObserver.observe(el);
      return;
    }
    prepare(el);
    revealObserver.observe(el);
  };

  /* ------------------------------------------------------------------
   * Count up
   * ------------------------------------------------------------------ */
  const numberFormat = (decimals) => {
    try {
      return new Intl.NumberFormat(root.lang || 'cs', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } catch (e) {
      return { format: (n) => n.toFixed(decimals) };
    }
  };

  const countUp = (el) => {
    const to = parseFloat(el.dataset.countTo ?? el.textContent.replace(/[^\d.,-]/g, '').replace(',', '.'));
    if (Number.isNaN(to)) return;
    const from = parseFloat(el.dataset.countFrom || 0);
    const decimals = parseInt(el.dataset.countDecimals || 0, 10);
    const duration = parseInt(el.dataset.countDuration || 1400, 10);
    const fmt = numberFormat(decimals);
    const prefix = el.dataset.countPrefix || '';
    const suffix = el.dataset.countSuffix || '';
    const render = (value) => {
      el.textContent = `${prefix}${fmt.format(value)}${suffix}`;
    };

    if (motionOff()) {
      render(to);
      return;
    }

    /* The server renders the final value; keep its width while shorter in-between values (0, 1, 12...) count up,
       so neighbours in an auto-sized row (nl-pdp2-stats--lg) do not jump sideways during scroll. */
    const lockWidth = el.getBoundingClientRect().width;
    if (lockWidth) el.style.minWidth = `${Math.ceil(lockWidth)}px`;

    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      render(from + (to - from) * ease(t));
      if (t < 1) requestAnimationFrame(tick);
      else el.style.minWidth = '';
    };
    requestAnimationFrame(tick);
  };

  const countObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              countObserver.unobserve(entry.target);
              countUp(entry.target);
            });
          },
          { threshold: 0.6 }
        )
      : null;

  const observeCount = (el) => {
    if (el.dataset.countBound) return;
    el.dataset.countBound = '1';
    if (!countObserver) {
      countUp(el);
      return;
    }
    countObserver.observe(el);
  };

  /* ------------------------------------------------------------------
   * Magnetic CTAs
   * ------------------------------------------------------------------ */
  const bindMagnetic = (el) => {
    if (el.dataset.magneticBound) return;
    el.dataset.magneticBound = '1';
    const strength = parseFloat(el.dataset.magnetic || 0.22);
    const max = 6;

    el.addEventListener('pointermove', (event) => {
      if (motionOff() || !mqFine.matches || event.pointerType !== 'mouse') return;
      const rect = el.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      const x = Math.max(-max, Math.min(max, dx * strength));
      const y = Math.max(-max, Math.min(max, dy * strength * 1.2));
      el.classList.remove('is-magnet-out');
      el.style.setProperty('--mx', `${x.toFixed(2)}px`);
      el.style.setProperty('--my', `${y.toFixed(2)}px`);
    });

    el.addEventListener('pointerleave', () => {
      el.classList.add('is-magnet-out');
      el.style.setProperty('--mx', '0px');
      el.style.setProperty('--my', '0px');
    });
  };

  /* ------------------------------------------------------------------
   * Scan
   * ------------------------------------------------------------------ */
  const scan = (scope = document) => {
    if (!scope || !scope.querySelectorAll) return;
    const each = (selector, fn) => {
      if (scope.matches && scope.matches(selector)) fn(scope);
      scope.querySelectorAll(selector).forEach(fn);
    };
    each(REVEAL_SELECTOR, observeReveal);
    each('[data-count-up]', observeCount);
    // Magnetic pull on CTAs disabled 2026-10-02 at owner request (buttons must not move on hover).
    // each('[data-magnetic]', bindMagnetic);
  };

  /* ------------------------------------------------------------------
   * Header: solid on scroll, hide on scroll down, show on scroll up
   * ------------------------------------------------------------------ */
  const initHeader = () => {
    // Past this scroll position the bar is solid on every template (transparent only at the top of the home hero)
    const SOLID_AT = 24;
    let header = null;
    let section = null;
    let hideOnScroll = false;
    let lastY = window.scrollY;
    let ticking = false;
    let hidden = false;
    // Header height from the ResizeObserver (computed after layout), so boot and scroll never force a layout
    let headerH = 0;
    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver((entries) => {
            const entry = entries[entries.length - 1];
            const box = entry && entry.borderBoxSize && entry.borderBoxSize[0];
            headerH = box ? box.blockSize : section ? section.getBoundingClientRect().height : 0;
            setOffset();
          })
        : null;

    const setOffset = () => {
      if (!section) return;
      const h = hidden ? 0 : headerH || section.getBoundingClientRect().height;
      root.style.setProperty('--nl-header-offset', `${Math.max(0, Math.round(h))}px`);
    };

    const show = () => {
      if (!hidden || !section) return;
      hidden = false;
      section.removeAttribute('data-hidden');
      setOffset();
    };

    const isLocked = () =>
      header.querySelector('[data-mega-open], .nl-nav__item[data-open]') !== null ||
      header.querySelector(':focus-visible') !== null ||
      document.querySelector('.nl-dialog[open]') !== null;

    // Direction is judged on the distance travelled since the last change of direction, so slow trackpad and
    // smooth scrolling (a few px per frame) hide and reveal the bar as reliably as a mouse wheel
    const TRAVEL = 12;
    let travel = 0;

    const update = () => {
      ticking = false;
      if (!section || !section.isConnected) return;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      section.toggleAttribute('data-scrolled', y > SOLID_AT);

      if (delta !== 0) travel = Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;

      if (hideOnScroll && (headerH || !resizeObserver)) {
        const threshold = (headerH || section.offsetHeight) + 120;
        if (y <= threshold || travel < -TRAVEL) {
          show();
        } else if (travel > TRAVEL && !isLocked() && !hidden) {
          hidden = true;
          section.setAttribute('data-hidden', '');
          setOffset();
        }
      }
      lastY = y;
    };

    // (Re)bind to the current header element: also runs after the theme editor re-renders the section
    const bind = () => {
      header = document.querySelector('[data-nl-header]');
      if (resizeObserver && section) resizeObserver.unobserve(section);
      section = header ? header.closest('.shopify-section') || header : null;
      if (!header) return;
      hideOnScroll = header.hasAttribute('data-hide-on-scroll');
      hidden = false;
      header.addEventListener('focusin', show);
      update();
      // The observer reports the size right after the next layout (and sets the offset); without it measure now
      if (resizeObserver) resizeObserver.observe(section);
      else setOffset();
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target && event.target.querySelector && event.target.querySelector('[data-nl-header]')) bind();
    });

    // Back / forward cache and restored scroll positions do not always fire a scroll event
    window.addEventListener('pageshow', () => {
      lastY = window.scrollY;
      travel = 0;
      update();
    });

    bind();
  };

  /* ------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------ */
  const boot = () => {
    scan(document);
    booting = false;
    // From the first IntersectionObserver answer on, CSS may hide not yet revealed elements (all below the fold)
    if (bootPending <= 0) markReady();
    initHeader();

    if ('MutationObserver' in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) => m.addedNodes.forEach((node) => node.nodeType === 1 && scan(node)));
      }).observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  };

  const api = {
    get reduced() {
      return motionOff();
    },
    reveal: scan,
    countUp,
  };
  NL.motion = api;

  mqReduced.addEventListener?.('change', () => {
    if (mqReduced.matches) {
      document.querySelectorAll(REVEAL_SELECTOR).forEach((el) => el.classList.add('is-revealed', 'is-reveal-done'));
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
